import { useTranslation } from "react-i18next";
import { FeedBotAnimatedMark } from "../brand/FeedBotAnimatedMark.js";
import { FeedBotGridBackdrop } from "../brand/FeedBotGridBackdrop.js";
import { motion, useReducedMotion } from "../../lib/motion.js";

const DOTS = [0, 1, 2] as const;

function SequentialDots({ reduceMotion }: { reduceMotion: boolean }): React.JSX.Element {
  return (
    <span className="flex items-center gap-2" aria-hidden="true">
      {DOTS.map((index) => (
        <motion.span
          key={index}
          className="size-2 rounded-full bg-brand"
          animate={
            reduceMotion
              ? { opacity: 0.55, scale: 1 }
              : { opacity: [0.28, 1, 0.28], scale: [0.82, 1, 0.82] }
          }
          transition={
            reduceMotion
              ? { duration: 0 }
              : {
                  duration: 1.2,
                  repeat: Infinity,
                  delay: index * 0.16,
                  ease: "easeInOut",
                }
          }
        />
      ))}
    </span>
  );
}

/**
 * Full-viewport identity loader while the server session (and then
 * organization selection) is unresolved. Must never reuse the login chrome
 * or simulate the authenticated app. No visible text; the status is for
 * assistive technology only.
 */
export function AppBootstrapLoader({
  label,
}: {
  label?: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion() ?? false;
  const statusLabel = label ?? t("app.loading");

  return (
    <div
      className="relative isolate grid h-dvh min-h-0 w-full min-w-0 place-items-center overflow-hidden bg-[#f8faf9] px-6 pt-[max(1.5rem,env(safe-area-inset-top,0px))] pb-[max(1.5rem,env(safe-area-inset-bottom,0px))]"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <FeedBotGridBackdrop />
      <span className="sr-only">{statusLabel}</span>
      <div className="flex flex-col items-center gap-7">
        <FeedBotAnimatedMark className="size-20" />
        <SequentialDots reduceMotion={reduceMotion} />
      </div>
    </div>
  );
}
